/* Synchronize the speaking-notes page with its Slide Studio deck. */
(() => {
  "use strict";
  const sections = [...document.querySelectorAll(".sn-section")];
  const cues = [...document.querySelectorAll(".sn-slide-cue[data-slide-index]")];
  const links = [...document.querySelectorAll(".sn-rail a")];
  const status = document.querySelector(".sn-status");
  const channelName = document.body.dataset.channel;
  const channel = channelName && "BroadcastChannel" in window ? new BroadcastChannel(channelName) : null;
  const colors = ["#66551f", "#28543a", "#244d66", "#61384f", "#6a4227"].map((fallback, index) => getComputedStyle(document.body).getPropertyValue(`--highlight-${index + 1}`).trim() || fallback);
  const palette = document.createElement("div");
  palette.className = "sn-highlight-palette";
  palette.setAttribute("role", "dialog");
  palette.setAttribute("aria-label", "Highlight color");
  document.body.appendChild(palette);
  let active = -1;
  let audience = 0;
  let pendingRange = null;
  let selectionTimer = null;
  if (new URLSearchParams(location.search).has("speaker")) {
    document.documentElement.classList.add("speaker-embed");
    document.body.classList.add("speaker-embed");
  }
  const send = message => {
    if (window.parent !== window) window.parent.postMessage(message, "*");
    else if (window.opener && !window.opener.closed) window.opener.postMessage(message, "*");
  };
  const cueContent = cue => cue?.querySelector(".sn-slide-content");
  function ensureTableCards(root) {
    root?.querySelectorAll(".sn-slide-body table").forEach(table => {
      if (table.parentElement?.classList.contains("ss-table-card")) return;
      const card = document.createElement("div");
      card.className = "ss-table-card";
      table.before(card);
      card.appendChild(table);
    });
  }
  function ensureQuestionResponses(cue) {
    if (!cue?.classList.contains("layout-questions")) return;
    cue.querySelectorAll(".sn-slide-body ol > li").forEach((item, position) => {
      if (item.querySelector(":scope > .ss-question-response")) return;
      const response = document.createElement("div");
      response.className = "ss-question-response";
      response.contentEditable = "true";
      response.setAttribute("role", "textbox");
      response.setAttribute("aria-label", `Response to question ${position + 1}`);
      response.spellcheck = true;
      item.appendChild(response);
    });
  }
  function slideHTML(cue) {
    const title = cue?.querySelector("h3")?.innerHTML || "";
    const body = cue?.querySelector(".sn-slide-body")?.innerHTML || "";
    return `<h1>${title}</h1><div class="ss-body">${body}</div>`;
  }
  function syncCue(cue) {
    if (!cue) return;
    send({type:"notes-slide-html", index:Number(cue.dataset.slideIndex), html:slideHTML(cue)});
  }
  function updateCue(index, html) {
    const cue = cues[index];
    if (!cue) return;
    // The deck echoes note edits back through the synchronization channel.
    // Replacing a focused cue's HTML would destroy its selection and move the
    // caret to the beginning after every keystroke.
    if (cue.contains(document.activeElement)) return;
    if (slideHTML(cue) === html) return;
    const template = document.createElement("template");
    template.innerHTML = html;
    const title = template.content.querySelector("h1");
    const body = template.content.querySelector(".ss-body");
    if (title) cue.querySelector("h3").innerHTML = title.innerHTML;
    if (body) cue.querySelector(".sn-slide-body").innerHTML = body.innerHTML;
    ensureTableCards(cue);
    ensureQuestionResponses(cue);
  }
  function applyHighlight(color) {
    if (!pendingRange) return;
    const cue = pendingRange.commonAncestorContainer.parentElement?.closest(".sn-slide-cue");
    const mark = document.createElement("mark");
    mark.style.background = color;
    try { pendingRange.surroundContents(mark); }
    catch { mark.appendChild(pendingRange.extractContents()); pendingRange.insertNode(mark); }
    window.getSelection()?.removeAllRanges();
    pendingRange = null;
    palette.classList.remove("is-visible");
    syncCue(cue);
  }
  function selectionContent(selection) {
    const node = selection?.anchorNode;
    const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
    return element?.closest(".sn-slide-content");
  }
  function openHighlightPalette() {
    const selection = window.getSelection();
    const content = selectionContent(selection);
    if (selection && !selection.isCollapsed && selection.rangeCount && content)
      pendingRange = selection.getRangeAt(0).cloneRange();
    if (!pendingRange) return;
    const rect = pendingRange.getBoundingClientRect();
    palette.style.left = `${Math.max(8, Math.min(innerWidth - 238, rect.left + rect.width / 2 - 110))}px`;
    palette.style.top = `${Math.max(8, rect.top - 54)}px`;
    palette.classList.add("is-visible");
  }
  colors.forEach((color, index) => {
    const button = document.createElement("button");
    button.className = "sn-highlight-color";
    button.style.background = color;
    button.textContent = String(index + 1);
    button.setAttribute("aria-label", `Highlight color ${index + 1}`);
    button.addEventListener("click", () => applyHighlight(color));
    palette.appendChild(button);
  });
  cues.forEach(cue => {
    ensureTableCards(cue);
    ensureQuestionResponses(cue);
    const content = cueContent(cue);
    if (content && cue.dataset.editable === "true") content.contentEditable = "true";
    content?.addEventListener("input", () => syncCue(cue));
    cue.addEventListener("click", event => {
      const mark = event.target.closest("mark");
      if (mark) { mark.replaceWith(...mark.childNodes); syncCue(cue); }
      send({type:"go", index:Number(cue.dataset.slideIndex)});
    });
  });
  document.addEventListener("selectionchange", () => {
    const selection = window.getSelection();
    const content = selectionContent(selection);
    clearTimeout(selectionTimer);
    if (selection && !selection.isCollapsed && selection.rangeCount && content) {
      pendingRange = selection.getRangeAt(0).cloneRange();
      selectionTimer = setTimeout(openHighlightPalette, 140);
    }
  });
  addEventListener("keydown", event => {
    if (event.target.closest?.('input,textarea,[contenteditable="true"]')) return;
    if (document.body.classList.contains("speaker-embed") && event.key.toLowerCase() === "b" && !event.repeat && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault(); send({type:"command", command:"blackout"});
    } else if (palette.classList.contains("is-visible") && /^[1-5]$/.test(event.key)) {
      event.preventDefault();
      applyHighlight(colors[Number(event.key) - 1]);
    } else if (event.key === "Escape") {
      pendingRange = null;
      palette.classList.remove("is-visible");
    }
  });
  function show(index, scroll = true, audienceChange = false) {
    index = Math.max(0, Math.min(Math.max(0, cues.length - 1), Number(index) || 0));
    const cue = cues[index];
    const section = cue?.closest(".sn-section") || sections[index];
    if (audienceChange) audience = index;
    const audienceCue = cues[audience];
    const audienceSection = audienceCue?.closest(".sn-section") || sections[audience];
    cues.forEach(item => item.classList.toggle("is-active", item === audienceCue));
    sections.forEach(item => item.classList.toggle("is-active", item === audienceSection));
    links.forEach(link => link.classList.toggle("is-active", link.hash === `#${section?.id}`));
    if (scroll && index !== active) (cue || section)?.scrollIntoView({behavior:matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",block:"center"});
    active = index;
    if (status) status.textContent = `${index + 1} / ${cues.length || sections.length}`;
  }
  function receive(message) {
    if (message?.type === "state") { show(message.index, message.index !== audience, true); if(status) status.textContent += ` · Step ${message.stepIndex ?? 0} / ${message.stepCount ?? 0}`; }
    else if (message?.type === "notes-step") show(active + Number(message.delta || 0));
    else if (message?.type === "slide-html") updateCue(Number(message.index), message.html);
    else if (message?.type === "notes-highlight") openHighlightPalette();
    else if (message?.type === "notes-clear-highlights") {
      const cue = cues[active];
      cue?.querySelectorAll("mark").forEach(mark => mark.replaceWith(...mark.childNodes));
      syncCue(cue);
    }
  }
  channel && (channel.onmessage = event => receive(event.data));
  addEventListener("message", event => receive(event.data));
  show(0, false, true);
  send({type:"notes-ready"});
})();
